import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import geografiaFunciones from "../function/geografia.Funciones";

const RegistrarEnteConformador = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const isEdit = useMemo(() => Boolean(id), [id]);

  const [nitEnteconformador, setNitEnteconformador] = useState("");
  const [coddaneEnteconformador, setCoddaneEnteconformador] = useState("");
  const [nombreEnteconformador, setNombreEnteconformador] = useState("");
  const [direccionEnteconformador, setDireccionEnteconformador] = useState("");
  const [telefono1Enteconformador, setTelefono1Enteconformador] = useState("");
  const [telefono2Enteconformador, setTelefono2Enteconformador] = useState("");
  const [correoEnteconformador, setCorreoEnteconformador] = useState("");
  const [zonaEnteconformador, setZonaEnteconformador] = useState("");

  // Ubicación
  const [paises, setPaises] = useState([]);
  const [estados, setEstados] = useState([]);
  const [ciudades, setCiudades] = useState([]);

  const [idPais, setIdPais] = useState("");
  const [idEstado, setIdEstado] = useState("");
  const [idCiudad, setIdCiudad] = useState("");

  const [paisSeleccionado, setPaisSeleccionado] = useState(null);
  const [estadoSeleccionado, setEstadoSeleccionado] = useState(null);
  const [ciudadSeleccionada, setCiudadSeleccionada] = useState(null);

  // ✅ estilos para evitar que el menú del Select se “corte”
  const selectStyles = useMemo(
    () => ({
      container: (base) => ({ ...base, width: "100%" }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      menu: (base) => ({ ...base, zIndex: 9999 }),
      control: (base, state) => ({
        ...base,
        minHeight: 38,
        borderColor: state.isFocused ? "#86b7fe" : base.borderColor,
        boxShadow: state.isFocused ? "0 0 0 .25rem rgba(13,110,253,.15)" : "none",
      }),
    }),
    []
  );

  const paisOptions = useMemo(
    () =>
      Array.from(
        new Map(
          (paises || []).map((p) => [
            p.idPais,
            { value: String(p.idPais), label: p.nombrePais },
          ])
        ).values()
      ),
    [paises]
  );

  const estadoOptions = useMemo(
    () =>
      Array.from(
        new Map(
          (estados || []).map((e) => [
            e.idEstado,
            { value: String(e.idEstado), label: e.nombreEstado },
          ])
        ).values()
      ),
    [estados]
  );

  const ciudadOptions = useMemo(
    () =>
      Array.from(
        new Map(
          (ciudades || []).map((c) => [
            c.idCiudad,
            { value: String(c.idCiudad), label: c.nombreCiudad },
          ])
        ).values()
      ),
    [ciudades]
  );

  const cleanData = useCallback(() => {
    setNitEnteconformador("");
    setCoddaneEnteconformador("");
    setNombreEnteconformador("");
    setDireccionEnteconformador("");
    setTelefono1Enteconformador("");
    setTelefono2Enteconformador("");
    setCorreoEnteconformador("");
    setZonaEnteconformador("");

    setIdPais("");
    setIdEstado("");
    setIdCiudad("");

    setPaisSeleccionado(null);
    setEstadoSeleccionado(null);
    setCiudadSeleccionada(null);

    setEstados([]);
    setCiudades([]);
  }, []);

  // Cargar países al inicio
  useEffect(() => {
    const cargarPaises = async () => {
      try {
        const paisesData = await geografiaFunciones.obtenerPaises();
        setPaises(paisesData || []);
      } catch (err) {
        console.error("Error al cargar países:", err);
      }
    };
    cargarPaises();
  }, []);

  // Cargar datos si es edición
  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const res = await axios.get(`/api/ente/listid/${id}`);
      if (!res.data?.ok) return;

      const ente = res.data.enteConformador;

      setNitEnteconformador(ente?.nitEnteconformador || "");
      setCoddaneEnteconformador(ente?.coddaneEnteconformador || "");
      setNombreEnteconformador(ente?.nombreEnteconformador || "");
      setDireccionEnteconformador(ente?.direccionEnteconformador || "");
      setTelefono1Enteconformador(ente?.telefono1Enteconformador || "");
      setTelefono2Enteconformador(ente?.telefono2Enteconformador || "");
      setCorreoEnteconformador(ente?.correoEnteconformador || "");
      setZonaEnteconformador(ente?.zonaEnteconformador || "");

      // Geografía
      const paisesData = await geografiaFunciones.obtenerPaises();
      setPaises(paisesData || []);

      const estadosData = ente?.idPais
        ? await geografiaFunciones.obtenerEstadosPorPais(ente.idPais)
        : [];
      const estadosUnicos = Array.from(
        new Map((estadosData || []).map((e) => [e.idEstado, e])).values()
      );
      setEstados(estadosUnicos);

      const ciudadesData = ente?.idEstado
        ? await geografiaFunciones.obtenerCiudadesPorEstado(ente.idEstado)
        : [];
      const ciudadesUnicas = Array.from(
        new Map((ciudadesData || []).map((c) => [c.idCiudad, c])).values()
      );
      setCiudades(ciudadesUnicas);

      setIdPais(String(ente?.idPais || ""));
      setIdEstado(String(ente?.idEstado || ""));
      setIdCiudad(String(ente?.idCiudad || ""));

      const paisObj = (paisesData || []).find(
        (p) => String(p.idPais) === String(ente?.idPais)
      );
      setPaisSeleccionado(
        paisObj ? { value: String(paisObj.idPais), label: paisObj.nombrePais } : null
      );

      const estadoObj = estadosUnicos.find(
        (e) => String(e.idEstado) === String(ente?.idEstado)
      );
      setEstadoSeleccionado(
        estadoObj
          ? { value: String(estadoObj.idEstado), label: estadoObj.nombreEstado }
          : null
      );

      const ciudadObj = ciudadesUnicas.find(
        (c) => String(c.idCiudad) === String(ente?.idCiudad)
      );
      setCiudadSeleccionada(
        ciudadObj
          ? { value: String(ciudadObj.idCiudad), label: ciudadObj.nombreCiudad }
          : null
      );
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "No se pudo cargar el ente conformador", "error");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) getData();
  }, [id, getData]);

  const handlePaisChange = async (selectedOption) => {
    setPaisSeleccionado(selectedOption);
    const newIdPais = selectedOption?.value ? String(selectedOption.value) : "";
    setIdPais(newIdPais);

    setEstadoSeleccionado(null);
    setCiudadSeleccionada(null);
    setIdEstado("");
    setIdCiudad("");
    setEstados([]);
    setCiudades([]);

    if (newIdPais) {
      const estadosData = await geografiaFunciones.obtenerEstadosPorPais(newIdPais);
      const estadosUnicos = Array.from(
        new Map((estadosData || []).map((e) => [e.idEstado, e])).values()
      );
      setEstados(estadosUnicos);
    }
  };

  const handleEstadoChange = async (selectedOption) => {
    setEstadoSeleccionado(selectedOption);
    const newIdEstado = selectedOption?.value ? String(selectedOption.value) : "";
    setIdEstado(newIdEstado);

    setCiudadSeleccionada(null);
    setIdCiudad("");
    setCiudades([]);

    if (newIdEstado) {
      const ciudadesData = await geografiaFunciones.obtenerCiudadesPorEstado(newIdEstado);
      const ciudadesUnicas = Array.from(
        new Map((ciudadesData || []).map((c) => [c.idCiudad, c])).values()
      );
      setCiudades(ciudadesUnicas);
    }
  };

  const handleCiudadChange = (selectedOption) => {
    setCiudadSeleccionada(selectedOption);
    setIdCiudad(selectedOption?.value ? String(selectedOption.value) : "");
  };

  const saveEnteConformador = async () => {
    try {
      setLoading(true);

      const newEnteConformador = {
        nitEnteconformador,
        coddaneEnteconformador,
        nombreEnteconformador,
        direccionEnteconformador,
        telefono1Enteconformador,
        telefono2Enteconformador,
        correoEnteconformador,
        zonaEnteconformador,
        idPais,
        idEstado,
        idCiudad,
      };

      const { data } = await axios.post("/api/ente/add", newEnteConformador);

      if (data?.ok === false) {
        return Swal.fire("Error", data?.message || "No se pudo registrar.", "error");
      }

      await Swal.fire({
        icon: "success",
        title: "El Ente Conformador ha sido registrado con éxito",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData(); // ✅ al guardar
      navigate("/enteconformador/ente/");
    } catch (error) {
      const msg = error.response?.data?.message || "Error al guardar el Ente Conformador";
      Swal.fire("Error", msg, "error");
      console.error("Error en saveEnteConformador:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateEnteConformador = async () => {
    try {
      setLoading(true);

      const enteConformadorUpdate = {
        nitEnteconformador,
        coddaneEnteconformador,
        nombreEnteconformador,
        direccionEnteconformador,
        telefono1Enteconformador,
        telefono2Enteconformador,
        correoEnteconformador,
        zonaEnteconformador,
        idPais,
        idEstado,
        idCiudad,
      };

      const { data } = await axios.put(`/api/ente/update/${id}`, enteConformadorUpdate);

      if (data?.ok === false) {
        return Swal.fire("Error", data?.message || "No se pudo actualizar.", "error");
      }

      await Swal.fire({
        icon: "success",
        title: data?.message || "EnteConformador actualizado exitosamente",
        showConfirmButton: false,
        timer: 1500,
      });

      cleanData(); // ✅ al actualizar
      navigate("/enteconformador/ente");
    } catch (error) {
      const msg = error.response?.data?.message || "No se pudo actualizar el EnteConformador";
      Swal.fire("Error", msg, "error");
      console.error("Error al actualizar el EnteConformador:", error);
    } finally {
      setLoading(false);
    }
  };

  const actions = async (e) => {
    e.preventDefault();
    isEdit ? await updateEnteConformador() : await saveEnteConformador();
  };

  return (
    <div className="bg-body-tertiary py-4">
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">
          {/* Header */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">
            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  {isEdit ? "Editar Ente Conformador" : "Registrar Ente Conformador"}
                </h5>
                <small className="text-secondary">
                  Completa la información del ente y su ubicación geográfica.
                </small>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-outline-dark"
                  onClick={() => navigate(-1)}
                  disabled={loading}
                  title="Volver">
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>
                  Volver
                </button>
              </div>
            </div>
          </div>

          {/* Body */}
          <div className="p-3 p-md-4">
            {loading && (
              <div className="text-center text-secondary py-3">
                <div className="spinner-border" role="status" />
                <div className="mt-2">Procesando…</div>
              </div>
            )}

            <form id="form-ente" onSubmit={actions} className="needs-validation" noValidate>
              <div className="row g-3">
                {/* DANE */}
                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">DANE</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ej: 05001"
                    value={coddaneEnteconformador}
                    onChange={(e) => setCoddaneEnteconformador(e.target.value)}
                    required
                  />
                </div>

                {/* NIT */}
                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">NIT</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ej: 900123456-7"
                    value={nitEnteconformador}
                    onChange={(e) => setNitEnteconformador(e.target.value)}
                    required
                  />
                </div>

                {/* Nombre */}
                <div className="col-12 col-md-6">
                  <label className="form-label fw-semibold">Nombre</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ej: Institución Educativa…"
                    value={nombreEnteconformador}
                    onChange={(e) => setNombreEnteconformador(e.target.value)}
                    required
                  />
                </div>

                {/* Dirección */}
                <div className="col-12 col-md-6">
                  <label className="form-label fw-semibold">Dirección</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ej: Calle 10 # 20-30"
                    value={direccionEnteconformador}
                    onChange={(e) => setDireccionEnteconformador(e.target.value)}
                    required
                  />
                </div>

                {/* Teléfonos */}
                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Teléfono 1</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ej: 6041234567"
                    value={telefono1Enteconformador}
                    onChange={(e) => setTelefono1Enteconformador(e.target.value)}
                    required
                  />
                </div>

                <div className="col-12 col-md-3">
                  <label className="form-label fw-semibold">Teléfono 2</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Opcional"
                    value={telefono2Enteconformador}
                    onChange={(e) => setTelefono2Enteconformador(e.target.value)}
                  />
                </div>

                {/* Ubicación */}
                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">País</label>
                  <Select
                    options={paisOptions}
                    value={paisSeleccionado}
                    onChange={handlePaisChange}
                    placeholder="Seleccione un país"
                    isClearable
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">Estado / Departamento</label>
                  <Select
                    options={estadoOptions}
                    value={estadoSeleccionado}
                    onChange={handleEstadoChange}
                    placeholder="Seleccione un estado"
                    isClearable
                    isDisabled={!paisSeleccionado}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                  />
                </div>

                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">Ciudad</label>
                  <Select
                    options={ciudadOptions}
                    value={ciudadSeleccionada}
                    onChange={handleCiudadChange}
                    placeholder="Seleccione una ciudad"
                    isClearable
                    isDisabled={!estadoSeleccionado}
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                  />
                </div>

                {/* Correo */}
                <div className="col-12 col-md-8">
                  <label className="form-label fw-semibold">Correo Electrónico</label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="Ej: correo@dominio.com"
                    value={correoEnteconformador}
                    onChange={(e) => setCorreoEnteconformador(e.target.value)}
                  />
                </div>

                {/* Zona */}
                <div className="col-12 col-md-4">
                  <label className="form-label fw-semibold">Zona</label>
                  <select
                    className="form-select"
                    value={zonaEnteconformador}
                    onChange={(e) => setZonaEnteconformador(e.target.value)}
                    required
                  >
                    <option value="">Seleccione…</option>
                    <option value="urbano">Urbano</option>
                    <option value="rural">Rural</option>
                  </select>
                </div>

                {/* Footer botones */}
                <div className="col-12 pt-2">
                  <div className="d-flex flex-wrap gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-dark"
                      onClick={cleanData}
                      disabled={loading}
                      title="Limpiar"
                    >
                      <i className="fa-solid fa-eraser me-2"></i>
                      Limpiar
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => {
                        cleanData();
                        navigate("/enteconformador/ente/");
                      }}
                      disabled={loading}
                    >
                      <i className="fa-solid fa-xmark me-2"></i>
                      Cancelar
                    </button>

                    <button
                      type="submit"
                      form="form-ente"
                      className="btn btn-dark"
                      disabled={loading}
                      title={isEdit ? "Actualizar" : "Guardar"}
                    >
                      <i
                        className={`fa-solid ${loading ? "fa-spinner fa-spin" : "fa-floppy-disk"
                          } me-2`}
                      ></i>
                      {isEdit ? "Actualizar" : "Guardar"}
                    </button>
                  </div>
                </div>
              </div>
            </form>

          </div>
        </div>
      </div>
    </div>
  );
};

export default RegistrarEnteConformador;
